import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah, formatDate } from "@/lib/format";
import { UnitSelect, rid } from "@/components/UnitSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil, Printer } from "lucide-react";
import { toast } from "sonner";
import { DeliveryNotePrint } from "@/components/DeliveryNotePrint";

export default function Outbound() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [salesList, setSalesList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [salesId, setSalesId] = useState("");
  const [trxDate, setTrxDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([{ _k: rid(), product_id: "", qty: 1, unit: "" }]);
  const [printDoc, setPrintDoc] = useState(null);

  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([api.get("/outbound"), api.get("/products"), api.get("/sales-agents")]);
    setRows(a.data); setProducts(b.data); setSalesList(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const addItem = () => setItems([...items, { _k: rid(), product_id: "", qty: 1, unit: "" }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const setItem = (i, k, v) => {
    const n = [...items];
    n[i] = { ...n[i], [k]: v };
    if (k === "product_id") {
      const p = products.find(p => p.product_id === v);
      if (p) n[i].unit = p.unit;
    }
    setItems(n);
  };
  const total = items.reduce((a, it) => {
    const p = products.find(p => p.product_id === it.product_id);
    const factor = p?.packs?.find(pk => pk.unit === it.unit)?.factor || 1;
    return a + Number(it.qty || 0) * factor * Number(p?.cost_price || 0);
  }, 0);

  const save = async () => {
    try {
      const payload = { sales_id: salesId, notes, date: trxDate, items: items.filter(i => i.product_id && i.qty > 0) };
      if (editing) {
        await api.put(`/outbound/${editing.outbound_id}`, payload);
        toast.success("Barang keluar diperbarui");
      } else {
        await api.post("/outbound", payload);
        toast.success("Serah terima ke armada tersimpan");
      }
      setOpen(false); setEditing(null); load();
      setItems([{ _k: rid(), product_id: "", qty: 1, unit: "" }]); setNotes(""); setSalesId(""); setTrxDate(new Date().toISOString().slice(0, 10));
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const openEditOut = (r) => {
    setEditing(r);
    setSalesId(r.sales_id);
    setNotes(r.notes || "");
    setTrxDate(r.transaction_date || String(r.date).slice(0, 10));
    setItems(r.items.map(it => ({ _k: rid(), product_id: it.product_id, qty: it.qty, unit: it.unit })));
    setOpen(true);
  };

  const deleteOut = async (r) => {
    if (!window.confirm(`Hapus transaksi ${r.trx_no}? Barang kembali ke gudang dari armada ${r.sales_name}.`)) return;
    try { await api.delete(`/outbound/${r.outbound_id}`); toast.success("Transaksi dihapus — stok direversal"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus"); }
  };

  return (
    <div data-testid="page-outbound" className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Transaksi</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Barang Keluar ke Sales</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800" data-testid="btn-new-outbound" onClick={() => setEditing(null)}><Plus className="h-4 w-4 mr-1" />Serah Terima</Button></DialogTrigger>
          <DialogContent className="max-w-3xl">
            <DialogHeader><DialogTitle>{editing ? `Ubah ${editing.trx_no}` : "Serah Terima ke Sales"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Sales {editing && <span className="text-xs text-slate-400">(tidak dapat diganti saat edit)</span>}</Label>
                <Select value={salesId} onValueChange={setSalesId} disabled={!!editing}>
                  <SelectTrigger data-testid="input-sales"><SelectValue placeholder="Pilih sales" /></SelectTrigger>
                  <SelectContent>{salesList.map(s => <SelectItem key={s.sales_id} value={s.sales_id}>{s.code} · {s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tanggal Transaksi *</Label>
                <Input type="date" value={trxDate} onChange={(e) => setTrxDate(e.target.value)} required disabled={!!editing} data-testid="input-outbound-date" />
              </div>
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2 mt-2">Detail Barang</div>
              {items.map((it, i) => (
                <div key={it._k} className="grid grid-cols-[1fr_70px_90px_40px] gap-2 items-center mb-2">
                  <Select value={it.product_id} onValueChange={(v) => setItem(i, "product_id", v)}>
                    <SelectTrigger data-testid={`row-${i}-product`}><SelectValue placeholder="Pilih barang" /></SelectTrigger>
                    <SelectContent>{products.map(p => <SelectItem key={p.product_id} value={p.product_id}>{p.sku} · {p.name} (Stok: {p.stock_warehouse})</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} data-testid={`row-${i}-qty`} />
                  <UnitSelect product={products.find(p => p.product_id === it.product_id)} value={it.unit} onChange={(v) => setItem(i, "unit", v)} testid={`row-${i}-unit`} />
                  <Button variant="ghost" size="icon" onClick={() => removeItem(i)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addItem} data-testid="btn-add-row"><Plus className="h-3 w-3 mr-1" />Baris</Button>
            </div>
            <div className="mt-2"><Label>Keterangan</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
            <DialogFooter className="items-center">
              <div className="mr-auto text-sm">Nilai modal: <span className="font-mono font-bold">{rupiah(total)}</span></div>
              <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-save-outbound">Simpan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Card>
        <table className="w-full text-sm" data-testid="table-outbound">
          <thead className="bg-slate-50 text-xs font-mono uppercase tracking-wider text-slate-500 text-left">
            <tr><th className="py-2 px-4">No.</th><th>Tanggal</th><th>Sales</th><th>Kendaraan</th><th className="text-right">Total</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.outbound_id} className="border-b"><td className="py-2 px-4 font-mono text-xs">{r.trx_no}</td><td>{formatDate(r.date)}</td><td>{r.sales_name}</td><td className="font-mono text-xs">{r.vehicle_no}</td><td className="text-right font-mono">{rupiah(r.total)}</td>
                <td className="whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => setPrintDoc(r)} data-testid={`print-outbound-${r.trx_no}`}><Printer className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => openEditOut(r)} data-testid={`edit-outbound-${r.trx_no}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteOut(r)} data-testid={`delete-outbound-${r.trx_no}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-400">Belum ada transaksi</td></tr>}
          </tbody>
        </table>
      </Card>
      <DeliveryNotePrint doc={printDoc} kind="Pengiriman ke Armada" onClose={() => setPrintDoc(null)} />
    </div>
  );
}
